import { ChangeEvent, ComponentProps, ReactNode, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { formatValueAsCurrencyOnBlur } from 'utils/formatMoney';

type BaseControl = {
  name: string;
  errors: string[];
  touched: boolean;
}

type BaseProps = {
  label: string;
  group?: {
    className?: string;
  };
  children: ReactNode;
  control: BaseControl;
}

const BaseFormComponent = (props: BaseProps) => {
  const { control, label, children } = props;
  const { name, errors } = control;

  return (
    <Form.Group className="form-group mt-24">
      <Form.Label
        className="font-weight-bold"
        htmlFor={name}
      >
        {label}
      </Form.Label>
      {children}
      <Form.Control.Feedback type="invalid">
        {errors}
      </Form.Control.Feedback>
    </Form.Group>
  )
}

type SelectProps = Omit<BaseProps, 'children'> & {
  control: {
    options: { label: string, value: string }[];
    value: string;
    name: string;
    onChange: (e: ChangeEvent<HTMLSelectElement>) => any;
    onBlur?: (e: ChangeEvent<HTMLInputElement>) => any;
    className?: string;
    touched: boolean;
    errors: string[];
  }
}

const FormSelect = (props: SelectProps) => {
  const { control } = props;
  const { options, onChange, onBlur, value, name, touched, errors } = control;

  return (
    <BaseFormComponent {...props}>
      <Form.Select
        className="form-control"
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        // @ts-expect-error types do not exactly align
        onBlur={onBlur}
        isInvalid={touched && errors.length > 0}
      >
        <option value="">Select an option</option>
        {Object.values(options).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </BaseFormComponent>
  )
}

type InputProps = Omit<BaseProps, 'children'> & {
  control: {
    value: string;
    name: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => any;
    onBlur?: (e: ChangeEvent<HTMLInputElement>) => any;
    className?: string;
    touched: boolean;
    errors: string[];
    type?: string;
  }
}

const FormInput = (props: InputProps) => {
  const { control } = props;
  const { onBlur, onChange, value, name, type, touched, errors } = control;

  return (
    <BaseFormComponent {...props}>
      <Form.Control
        type={type || 'text'}
        className="form-control"
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        isInvalid={touched && errors.length > 0}
        // @ts-expect-error types do not exactly align
        onBlur={onBlur}
      />
    </BaseFormComponent>
  )
}

type CheckboxProps = Omit<BaseProps, 'children'> & {
  control: {
    checked: boolean;
    name: string;
    onChange: ComponentProps<typeof Form.Check>['onChange'];
    className?: string;
    touched: boolean;
    errors: string[];
  }
}

const FormCheck = (props: CheckboxProps) => {
  const { control } = props;
  const { onChange, checked, name } = control;
  
  return (
    <BaseFormComponent {...props}>
      <Form.Check
        id={name}
        name={name}
        checked={checked}
        onChange={onChange}
      />
    </BaseFormComponent>
  )
}

const FormCurrency = (props: InputProps) => {
  const { control } = props;
  const { onBlur, onChange, value } = control;

  const [formattedValue, setFormattedValue] = useState<string>('');

  useEffect(() => {
    setFormattedValue(`$${value}`);
  }, [value])

  return (
    <FormInput 
      {...props} 
      control={{ 
        ...control, 
        value: formattedValue,
        onChange: (e) => {
          e.target.value = e.target.value.replaceAll('$', '');
          onChange?.(e);
        },
        onBlur: (e) => {
          const value = formatValueAsCurrencyOnBlur(e.target.value);
          setFormattedValue(value);
          onBlur?.(e);
        }
      }}
    />
  )
}

export const AppForm = {
  Select: FormSelect,
  Input: FormInput,
  Check: FormCheck,
  Currency: FormCurrency,
}

export type AppFormSelectValue = ComponentProps<typeof AppForm.Select>['control']['value'];

export type AppFormInputValue = ComponentProps<typeof AppForm.Input>['control']['value'];
